/** Shows Meta token validity, expiry, and WhatsApp permission scopes from backend debug. */
const TokenPermissionsPanel = ({ tokenDebug, compact = false }) => {
  if (!tokenDebug) return null;

  if (tokenDebug.debug_skipped) {
    return (
      <div className={`rounded-xl border border-amber-200 bg-amber-50 ${compact ? "p-3 text-xs" : "p-4 text-sm"}`}>
        <p className="font-semibold text-amber-900">Token debug skipped</p>
        <p className="text-amber-800 mt-1">
          {tokenDebug.debug_skip_reason ||
            "Add Meta App ID + App Secret below to inspect token permissions. Not required for messaging or template sync."}
        </p>
      </div>
    );
  }

  const permissions = tokenDebug.permissions || [];
  const missing = tokenDebug.missing_required_scopes || [];

  return (
    <div className={`rounded-xl border border-gray-200 bg-gray-50 ${compact ? "p-3 text-xs" : "p-4 text-sm"}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${
            tokenDebug.is_valid !== false
              ? "bg-emerald-100 text-emerald-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {tokenDebug.is_valid !== false ? "Valid token" : "Invalid token"}
        </span>
        {tokenDebug.type && (
          <span className="text-gray-600">Type: {tokenDebug.type}</span>
        )}
        {tokenDebug.expires_at_human && (
          <span className="text-gray-600">Expires: {tokenDebug.expires_at_human}</span>
        )}
      </div>

      {permissions.length > 0 && (
        <div className="space-y-2">
          <p className="font-semibold text-gray-800">WhatsApp permissions</p>
          <ul className="space-y-1.5">
            {permissions.map((perm) => (
              <li
                key={perm.scope}
                className={`flex items-start gap-2 rounded-lg px-2.5 py-2 ${
                  perm.granted ? "bg-emerald-50 border border-emerald-100" : "bg-red-50 border border-red-100"
                }`}
              >
                <span className="flex-shrink-0 mt-0.5">
                  {perm.granted ? (
                    <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </span>
                <div className="min-w-0">
                  <p className={`font-medium ${perm.granted ? "text-emerald-900" : "text-red-900"}`}>
                    {perm.label}
                    {perm.required && <span className="text-gray-500 font-normal"> (required)</span>}
                  </p>
                  <p className="text-gray-600 text-xs mt-0.5">{perm.description}</p>
                  <p className="text-gray-400 font-mono text-[10px] mt-0.5">{perm.scope}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {missing.length > 0 && (
        <p className="mt-2 text-red-700 font-medium">
          Missing required: {missing.join(", ")}
        </p>
      )}

      {(tokenDebug.scopes?.length > 0 || tokenDebug.granular_scopes?.length > 0) && (
        <details className="mt-3">
          <summary className="cursor-pointer text-gray-600 font-medium">All token scopes</summary>
          <pre className="mt-2 text-[10px] bg-white border border-gray-200 rounded-lg p-2 overflow-x-auto">
            {JSON.stringify(
              { scopes: tokenDebug.scopes, granular_scopes: tokenDebug.granular_scopes },
              null,
              2
            )}
          </pre>
        </details>
      )}
    </div>
  );
};

export default TokenPermissionsPanel;
